import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { 
  ArrowLeftIcon, 
  CalendarDaysIcon, 
  BookmarkIcon, 
  PrinterIcon,
  MagnifyingGlassIcon,
  ListBulletIcon,
  XMarkIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import { TemaNormativo } from '../../../types/normatividad'
import normatividadService from '../../../services/normatividadService'

const PaginaVisorNormativa: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [documento, setDocumento] = useState<TemaNormativo | null>(null)
  
  // Estado para la UI
  const [sidebarOpen, setSidebarOpen] = useState(false) // Para móvil
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSection, setActiveSection] = useState<string>('')
  const [showScrollTop, setShowScrollTop] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchDoc = async () => {
      if (id) {
        try {
          const doc = await normatividadService.getNormaById(id)
          if (doc) {
            setDocumento(doc)
          } else {
            if (location.pathname.includes('/jefes/')) {
              navigate('/institucion/jefes')
            } else if (location.pathname.includes('/admin/')) {
              navigate('/admin/normatividad')
            } else if (location.pathname.includes('/dashboard/')) {
              navigate('/dashboard/normatividad')
            } else {
              navigate('/institucion/usuarios')
            }
          }
        } catch (error) {
          console.error("Error cargando documento", error)
        }
      }
    }
    fetchDoc()
  }, [id, navigate, location.pathname])

  // ScrollSpy para el índice
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) setShowScrollTop(true)
      else setShowScrollTop(false)

      // Lógica básica de ScrollSpy
      if (!documento?.indice) return
      
      const sections = documento.indice.map(item => document.getElementById(item.id_referencia))
      const scrollPosition = window.scrollY + 150

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(documento.indice[i].id_referencia)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [documento])

  // Buscador local (simple resaltado visual no persistente, idealmente sería manipular el DOM)
  // Nota: Para una implementación real robusta de búsqueda, se requeriría un parser más complejo.
  // Aquí simulamos el comportamiento filtrando el índice o haciendo scroll al primer match.
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchTerm || !contentRef.current) return
    
    // Funcionalidad básica: encontrar la primera ocurrencia visualmente
    // (En una app real, usaríamos una librería como mark.js)
    const win = window as any
    if (win.find && typeof win.find === 'function') {
      win.find(searchTerm)
    }
  }

  const handleBack = () => {
    if (location.pathname.includes('/jefes/')) {
      navigate('/institucion/jefes')
    } else if (location.pathname.includes('/admin/')) {
      navigate('/admin/normatividad')
    } else if (location.pathname.includes('/dashboard/')) {
      navigate('/dashboard/normatividad')
    } else {
      navigate('/institucion/usuarios')
    }
  }

  if (!documento) return null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      
      {/* --- Header Flotante / Sticky --- */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          
          <div className="flex items-center gap-4 overflow-hidden">
            <button 
              onClick={handleBack}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors shrink-0"
              title="Volver"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate leading-tight">
                {documento.titulo}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Buscador de escritorio */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center relative group">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3" />
              <input 
                type="text" 
                placeholder="Buscar en documento..." 
                className="pl-9 pr-4 py-1.5 text-sm bg-gray-100 dark:bg-gray-700/50 border-transparent focus:bg-white dark:focus:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full focus:ring-2 focus:ring-blue-500 w-48 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </form>

            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1 hidden sm:block"></div>

            <button 
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Guardar marcador"
            >
              <BookmarkIcon className="h-5 w-5" />
            </button>
            <button 
              onClick={() => window.print()}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg transition-colors hidden sm:block"
              title="Imprimir"
            >
              <PrinterIcon className="h-5 w-5" />
            </button>
            <button 
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg transition-colors md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex gap-8 relative">
        
        {/* --- Sidebar: Índice (Desktop Sticky) --- */}
        {documento.indice && (
          <aside className="hidden md:block w-64 shrink-0">
            <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 custom-scrollbar">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 px-2">
                Tabla de Contenido
              </h3>
              <nav className="space-y-0.5">
                {documento.indice.map((item, idx) => (
                  <a
                    key={idx}
                    href={`#${item.id_referencia}`}
                    onClick={(e) => {
                      e.preventDefault()
                      const el = document.getElementById(item.id_referencia)
                      if (el) {
                        const y = el.getBoundingClientRect().top + window.scrollY - 100 // Offset del header
                        window.scrollTo({ top: y, behavior: 'smooth' })
                      }
                    }}
                    className={`
                      block px-3 py-2 text-sm rounded-lg transition-all duration-200 border-l-2
                      ${activeSection === item.id_referencia
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-500 font-medium'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                      }
                      ${item.nivel === 2 ? 'ml-4 text-xs' : ''}
                    `}
                  >
                    {item.titulo}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* --- Contenido Principal --- */}
        <div className="flex-1 min-w-0">
          
          {/* Metadata Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`
                px-3 py-1 rounded-full text-xs font-bold border
                ${documento.tipo === 'LEY' ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' : ''}
                ${documento.tipo === 'REGLAMENTO' ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' : ''}
                ${documento.tipo === 'TRATADO' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' : ''}
              `}>
                {documento.tipo}
              </span>
              <span className="flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-md">
                <CalendarDaysIcon className="h-3.5 w-3.5 mr-1.5" />
                Actualizado: {documento.actualizado}
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight font-serif">
              {documento.titulo}
            </h2>
            <div 
              className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: documento.descripcion }}
            />
          </div>

          {/* Document Content */}
          <div 
            ref={contentRef}
            className="
              bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 md:p-12
              prose prose-lg prose-slate dark:prose-invert max-w-none
              prose-headings:font-serif prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
              prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-8 prose-p:font-serif
              prose-strong:text-blue-700 dark:prose-strong:text-blue-400 prose-strong:font-bold
              prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
              prose-li:text-gray-700 dark:prose-li:text-gray-300
            "
            dangerouslySetInnerHTML={{ __html: documento.contenido || '<p class="italic text-gray-500">Contenido no disponible para visualización rápida. Descargue el documento completo.</p>' }}
          />
          
        </div>
      </main>

      {/* --- Mobile Sidebar Overlay --- */}
      {sidebarOpen && documento.indice && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-900 shadow-2xl p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Índice</h3>
              <button onClick={() => setSidebarOpen(false)} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-2">
              {documento.indice.map((item, idx) => (
                <a
                  key={idx}
                  href={`#${item.id_referencia}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setSidebarOpen(false)
                    const el = document.getElementById(item.id_referencia)
                    if (el) {
                      const y = el.getBoundingClientRect().top + window.scrollY - 80
                      window.scrollTo({ top: y, behavior: 'smooth' })
                    }
                  }}
                  className={`block p-3 rounded-lg text-sm ${
                    activeSection === item.id_referencia
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {item.titulo}
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* --- Botón Scroll Top --- */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`
          fixed bottom-8 right-8 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 z-40
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}
      >
        <ChevronUpIcon className="h-6 w-6" />
      </button>

      {/* --- Estilos de Impresión --- */}
      <style>{`
        @media print {
          header, aside, button { display: none !important; }
          body { background: white !important; color: black !important; }
          .prose { max-width: 100% !important; padding: 0 !important; }
        }
        /* Custom Scrollbar for Sidebar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.5);
          border-radius: 20px;
        }
      `}</style>
    </div>
  )
}

export default PaginaVisorNormativa
